import { useQuery } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

export const BASE_QUERY_KEY = 'emailExecAssistant/briefs';

const BRIEFS_POLL_INTERVAL_MS = 5000;

// Mirrors the backend in-flight guard's age bound: a job stranded in Queued/InProgress by a crash
// stops blocking new runs server-side after 2h, so it must also stop driving polling/disabled state here.
const ACTIVE_RUN_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export const isTerminalBriefRunStatus = (status: Api.EmailExecBriefRunStatus | undefined) =>
	status === 'Success' || status === 'Failed';

export const isActiveBriefRun = (run: Api.IEmailExecBriefSystemJob): boolean => {
	if (isTerminalBriefRunStatus(run.status) || !run.dateCreatedUtc) {
		return false;
	}
	return Date.now() - new Date(run.dateCreatedUtc).getTime() < ACTIVE_RUN_MAX_AGE_MS;
};

export const useEmailExecBriefsQuery = ({ enabled, meta }: { enabled?: boolean; meta?: LevStandardQueryMeta } = {}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryKey: [BASE_QUERY_KEY],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.IEmailExecBriefSystemJob[]>(`emailExecAssistant/briefs`, 'GET'),
		refetchInterval: query => (query.state.data?.some(isActiveBriefRun) ? BRIEFS_POLL_INTERVAL_MS : false),
		meta,
	});
};

export const invalidateEmailExecBriefs = () => reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
